"use client";

import { useState, useCallback, useMemo, type ReactNode } from "react";
import { X, Lock, Loader2, Sparkles, KeyRound, ArrowLeft } from "lucide-react";
import { motion } from "motion/react";
import { useAnalyzeFileContext } from "./context/AnalyzeFileContext";
import { AnalyzeFileProvider } from "./context/AnalyzeFileContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { Button } from "@/modern/components/ui/button-shadcn";
import { useOpenAIKeyModal } from "@/integrations";
import { useBoardPermissions } from "@/react/components/entityViews/settings/hooks/useBoardPermissions";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { EntityRecord } from "@/types/entity";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { AnalyzeWorkspace } from "./components/AnalyzeWorkspace";
import { AnalyzeOverlaySync } from "./components/AnalyzeOverlaySync";
import type { AnalyzePresentation } from "./analyzeConstants";
import { cn } from "@/modern/utils";

export interface UnassignedInsightBoardOption {
  id: number;
  display_name: string;
}

interface AnalyzeDialogProps {
  entityRecord?: EntityRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file?: SwivuFile;
  unassignedFileId?: number;
  insightBoardId?: number;
  unassignedAiBoards?: UnassignedInsightBoardOption[];
  unassignedBoardsLoading?: boolean;
  onUnassignedDefaultBoardChange?: (boardId: number) => Promise<boolean>;
  presentation?: AnalyzePresentation;
  isDetached?: boolean;
  onToggleDetach?: () => void;
}

const AnalyzeCompactHeader = ({
  onClose,
  title,
  subtitle,
  embeddedInShell,
}: {
  onClose: () => void;
  title: string;
  subtitle?: string;
  embeddedInShell?: boolean;
}) => (
  <div
    className={cn(
      "flex shrink-0 items-center gap-2",
      embeddedInShell
        ? "border-b border-border/40 bg-transparent pb-2.5"
        : "border-b border-border bg-card px-3 py-2.5",
    )}
  >
    {embeddedInShell ? (
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="size-9 shrink-0 rounded-full"
        onClick={onClose}
        aria-label="Back to details"
      >
        <ArrowLeft className="w-4 h-4" />
      </Button>
    ) : (
      <Sparkles className="size-4 shrink-0 text-primary" />
    )}
    <div className="min-w-0 flex-1">
      <h2
        className={cn(
          "text-foreground",
          embeddedInShell ? "text-xl font-normal tracking-tight" : "text-sm font-semibold",
        )}
      >
        {title}
      </h2>
      {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
    </div>
    {!embeddedInShell ? (
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={onClose}
        aria-label="Close"
      >
        <X className="w-4 h-4" />
      </Button>
    ) : null}
  </div>
);

const PanelLoadingView = ({
  onClose,
  title,
  embeddedInShell,
}: {
  onClose: () => void;
  title: string;
  embeddedInShell?: boolean;
}) => (
  <div
    className={cn(
      "flex h-full min-h-0 flex-col",
      embeddedInShell ? "bg-transparent" : "bg-card",
    )}
  >
    <AnalyzeCompactHeader onClose={onClose} title={title} embeddedInShell={embeddedInShell} />
    <div className="flex flex-1 items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center gap-4 text-muted-foreground"
      >
        <div className="relative">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}>
            <Loader2 className="w-8 h-8" />
          </motion.div>
          <motion.div
            className="absolute inset-0 rounded-full"
            animate={{ opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              background: "radial-gradient(circle, hsl(var(--primary) / 0.2) 0%, transparent 70%)",
            }}
          />
        </div>
        <p className="text-sm font-medium">Loading advisors...</p>
      </motion.div>
    </div>
  </div>
);

const UnassignedInsightBoardBanner = ({
  boards,
  isLoading,
  selectedBoardId,
  onBoardChange,
}: {
  boards: UnassignedInsightBoardOption[];
  isLoading: boolean;
  selectedBoardId: number | null;
  onBoardChange: (boardId: number) => Promise<void>;
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = async (value: string) => {
    const id = Number(value);
    if (!Number.isFinite(id)) return;
    setIsSaving(true);
    try {
      await onBoardChange(id);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="shrink-0 px-3 py-2 border-b border-border flex items-center gap-2 text-xs text-muted-foreground bg-muted/30">
        <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
        Loading boards…
      </div>
    );
  }

  if (boards.length === 0) {
    return (
      <div className="shrink-0 px-3 py-2 border-b border-border text-xs text-muted-foreground bg-muted/30">
        No AI-enabled boards available.
      </div>
    );
  }

  return (
    <div className="shrink-0 px-3 py-2.5 border-b border-border bg-muted/30 space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
        <Sparkles className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span>Default AI board</span>
      </div>
      <Select
        value={selectedBoardId != null ? String(selectedBoardId) : ""}
        onValueChange={(val) => void handleChange(val)}
        disabled={isSaving}
      >
        <SelectTrigger className="h-8 w-full text-xs">
          <SelectValue placeholder={isSaving ? "Saving…" : "Select board"} />
        </SelectTrigger>
        <SelectContent className="z-[calc(var(--swivu-bryntum-z-index)+30)]">
          {boards.map((board) => (
            <SelectItem key={board.id} value={String(board.id)}>
              {board.display_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const LockedState = ({
  variant = "openai",
  onConfigureApiKey,
}: {
  variant?: "openai" | "openai-configure" | "select-insight-board";
  onConfigureApiKey?: () => void;
}) => {
  if (variant === "select-insight-board") {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-sm w-full text-center space-y-5">
          <div className="flex justify-center">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
              <Lock className="w-7 h-7 text-muted-foreground" />
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="text-base font-semibold text-foreground">Default AI board required</h3>
            <p className="text-sm text-muted-foreground">
              Choose a default AI-enabled board above so insights can run for this file.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isConfigurable = variant === "openai-configure";

  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="max-w-sm w-full text-center space-y-5">
        <div className="flex justify-center">
          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
            <Lock className="w-7 h-7 text-muted-foreground" />
          </div>
        </div>
        <div className="space-y-2">
          <h3 className="text-base font-semibold text-foreground">AI Key Required</h3>
          <p className="text-sm text-muted-foreground">
            {isConfigurable
              ? "An OpenAI API key needs to be added to use AI insights. Add your API key to enable AI-powered analysis."
              : "An OpenAI API key needs to be added to use AI insights. Only the Board Owner can configure the API key."}
          </p>
        </div>
        {isConfigurable && onConfigureApiKey && (
          <Button type="button" onClick={onConfigureApiKey} variant="outline" className="w-full">
            <KeyRound className="w-4 h-4 mr-2" />
            Configure API Key
          </Button>
        )}
      </div>
    </div>
  );
};

export const AnalyzePanelContent = ({
  onOpenChange,
  unassignedInsightMode,
  insightBoardId,
  unassignedAiBoards,
  unassignedBoardsLoading,
  onUnassignedDefaultBoardChange,
  presentation,
  fileName,
  isDetached,
  onToggleDetach,
}: {
  onOpenChange: (open: boolean) => void;
  unassignedInsightMode: boolean;
  insightBoardId: number | null;
  unassignedAiBoards?: UnassignedInsightBoardOption[];
  unassignedBoardsLoading?: boolean;
  onUnassignedDefaultBoardChange?: (boardId: number) => Promise<boolean>;
  presentation: AnalyzePresentation;
  fileName?: string;
  isDetached?: boolean;
  onToggleDetach?: () => void;
}) => {
  const { handleDeleteMessage, isLoadingAvatars } = useAnalyzeFileContext();
  const { userPreferences } = useSwivuBryntumContext();
  const { canManageSecrets } = useBoardPermissions();
  const openAiKeyModal = useOpenAIKeyModal();

  const { version: boardPreferencesVersion } = useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) =>
      record.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey ||
      record.key === PERSISTENCE_STATE_KEYS.openAiApiKey,
  });

  const [keySavedOverride, setKeySavedOverride] = useState(false);

  const hasOpenAIKey = useMemo(() => {
    if (!userPreferences) return false;
    return userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";
  }, [userPreferences, boardPreferencesVersion]);

  const hasApiKey = hasOpenAIKey || keySavedOverride;
  const insightFlowUnlocked = unassignedInsightMode ? insightBoardId != null : hasApiKey;
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  const handleApiKeySaved = useCallback(() => {
    setKeySavedOverride(true);
  }, []);

  const handleConfigureApiKey = useCallback(() => {
    openAiKeyModal?.open({ onSaved: handleApiKeySaved });
  }, [openAiKeyModal, handleApiKeySaved]);

  const unassignedBanner =
    unassignedInsightMode && onUnassignedDefaultBoardChange ? (
      <UnassignedInsightBoardBanner
        boards={unassignedAiBoards ?? []}
        isLoading={unassignedBoardsLoading ?? false}
        selectedBoardId={insightBoardId}
        onBoardChange={async (boardId) => {
          await onUnassignedDefaultBoardChange(boardId);
        }}
      />
    ) : undefined;

  const workspaceTitle = presentation === "modal" ? "AI Analysis" : "Analyze";
  const embeddedInShell = presentation === "panel" && !isDetached;

  if (presentation === "panel" && isLoadingAvatars) {
    return (
      <PanelLoadingView onClose={handleClose} title={workspaceTitle} embeddedInShell={embeddedInShell} />
    );
  }

  const workspace = (
    <AnalyzeWorkspace
      presentation={presentation}
      isLocked={false}
      onDeleteMessage={handleDeleteMessage}
      isLoadingOverlay={presentation === "modal" && isLoadingAvatars}
      onClose={handleClose}
      title={workspaceTitle}
      subtitle={presentation === "modal" ? fileName : undefined}
      isDetached={isDetached}
      onToggleDetach={onToggleDetach}
    />
  );

  const lockedContent = (
    <>
      <AnalyzeCompactHeader
        onClose={handleClose}
        title={workspaceTitle}
        subtitle={presentation === "modal" ? fileName : undefined}
        embeddedInShell={embeddedInShell}
      />
      {unassignedBanner}
      {unassignedInsightMode ? (
        <LockedState variant="select-insight-board" />
      ) : canManageSecrets ? (
        <LockedState variant="openai-configure" onConfigureApiKey={handleConfigureApiKey} />
      ) : (
        <LockedState variant="openai" />
      )}
    </>
  );

  const body = (
    <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
      {!insightFlowUnlocked ? lockedContent : (
        <>
          {unassignedBanner}
          {workspace}
        </>
      )}
    </div>
  );

  if (presentation === "panel") {
    return (
      <div
        className={cn(
          "flex h-full min-h-0 flex-col",
          // Docked in File Detail soft card — shell owns bg/radius; avoid a nested card look.
          isDetached ? "bg-card" : "bg-transparent",
        )}
      >
        {body}
      </div>
    );
  }

  return <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-card">{body}</div>;
};

export const AnalyzeFileShell = ({
  open,
  file,
  entityRecord,
  unassignedFileId,
  insightBoardId,
  children,
}: {
  open: boolean;
  file: SwivuFile;
  entityRecord?: EntityRecord;
  unassignedFileId?: number;
  insightBoardId?: number;
  children: ReactNode;
}) => (
  <AnalyzeFileProvider
    open={open}
    file={file}
    entityRecord={entityRecord}
    unassignedFileId={unassignedFileId}
    insightBoardId={insightBoardId}
  >
    <AnalyzeOverlaySync />
    {children}
  </AnalyzeFileProvider>
);

export const AnalyzePanel = ({
  open,
  onOpenChange,
  file,
  entityRecord,
  unassignedFileId,
  insightBoardId,
  unassignedAiBoards,
  unassignedBoardsLoading,
  onUnassignedDefaultBoardChange,
  isDetached,
  onToggleDetach,
}: AnalyzeDialogProps) => {
  if (!file || !open) return null;

  const unassignedInsightMode = unassignedFileId != null;

  return (
    <AnalyzeFileShell
      open={open}
      file={file}
      entityRecord={entityRecord}
      unassignedFileId={unassignedFileId}
      insightBoardId={insightBoardId}
    >
      <AnalyzePanelContent
        onOpenChange={onOpenChange}
        unassignedInsightMode={unassignedInsightMode}
        insightBoardId={insightBoardId ?? null}
        unassignedAiBoards={unassignedAiBoards}
        unassignedBoardsLoading={unassignedBoardsLoading}
        onUnassignedDefaultBoardChange={onUnassignedDefaultBoardChange}
        presentation="panel"
        isDetached={isDetached}
        onToggleDetach={onToggleDetach}
      />
    </AnalyzeFileShell>
  );
};

export const AnalyzeDialog = ({
  open,
  onOpenChange,
  file,
  entityRecord,
  unassignedFileId,
  insightBoardId,
  unassignedAiBoards,
  unassignedBoardsLoading,
  onUnassignedDefaultBoardChange,
  presentation = "modal",
}: AnalyzeDialogProps) => {
  const editorInstance = useMemo(() => ({ editorRef: null }), []);
  const dummyRecord = useMemo(() => ({ isPhantom: true }), []);

  const MODAL_SIZE_CONFIG = useMemo(
    () => ({
      width: "70vw",
      height: "70vh",
      minWidth: "min(70vw, 600px)",
      minHeight: "min(70vh, 500px)",
      maxWidth: "70vw",
      maxHeight: "100vh",
    }),
    [],
  );

  if (!file) return null;

  if (presentation === "panel") {
    return (
      <AnalyzePanel
        open={open}
        onOpenChange={onOpenChange}
        file={file}
        entityRecord={entityRecord}
        unassignedFileId={unassignedFileId}
        insightBoardId={insightBoardId}
        unassignedAiBoards={unassignedAiBoards}
        unassignedBoardsLoading={unassignedBoardsLoading}
        onUnassignedDefaultBoardChange={onUnassignedDefaultBoardChange}
        presentation="panel"
      />
    );
  }

  const unassignedInsightMode = unassignedFileId != null;
  const modalTitle = file.name ? `AI Analysis — ${file.name}` : "AI Analysis";

  return (
    <AnalyzeFileShell
      open={open}
      file={file}
      entityRecord={entityRecord}
      unassignedFileId={unassignedFileId}
      insightBoardId={insightBoardId}
    >
      {open && (
        <BaseModal
          title={modalTitle}
          description=""
          onClose={() => onOpenChange(false)}
          size={MODAL_SIZE_CONFIG}
          editorInstance={editorInstance}
          record={dummyRecord}
          headerIconCls=""
          modal={true}
          version=""
          hideMenu={true}
          headerRenderer={() => <></>}
        >
          <AnalyzePanelContent
            onOpenChange={onOpenChange}
            unassignedInsightMode={unassignedInsightMode}
            insightBoardId={insightBoardId ?? null}
            unassignedAiBoards={unassignedAiBoards}
            unassignedBoardsLoading={unassignedBoardsLoading}
            onUnassignedDefaultBoardChange={onUnassignedDefaultBoardChange}
            presentation="modal"
            fileName={file.name}
          />
        </BaseModal>
      )}
    </AnalyzeFileShell>
  );
};
